import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  LifeBuoy,
  RefreshCw,
  Search,
  AlertTriangle,
  CheckCircle2,
  Archive,
  MessageSquare,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";
import PageHeader from "@/components/PageHeader";
import SupportAttachmentViewer from "@/components/SupportAttachmentViewer";

const STATUS_OPTIONS = [
  { id: "all", label: "All active" },
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In Progress" },
  { id: "waiting_on_customer", label: "Waiting on Customer" },
  { id: "resolved", label: "Resolved" },
  { id: "closed", label: "Closed" },
  { id: "archived", label: "Archived" },
];

const CATEGORY_OPTIONS = [
  { id: "all", label: "All categories" },
  { id: "bug", label: "Bug / Error" },
  { id: "billing", label: "Billing issue" },
  { id: "social_connection", label: "Social connection issue" },
  { id: "publishing", label: "Publishing issue" },
  { id: "ai_generation", label: "AI generation issue" },
  { id: "feature_request", label: "Feature request" },
  { id: "other", label: "Other" },
];

const PRIORITY_OPTIONS = [
  { id: "all", label: "All priorities" },
  { id: "urgent", label: "Urgent" },
  { id: "high", label: "High" },
  { id: "normal", label: "Normal" },
  { id: "low", label: "Low" },
];

const STATUS_COLORS = {
  open: "text-[color:var(--accent-warm)] border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.08)]",
  in_progress: "text-[color:var(--brand-primary)] border-[color:rgba(27,168,159,0.40)] bg-[color:rgba(27,168,159,0.08)]",
  waiting_on_customer: "text-[color:var(--warning,#eab308)] border-[color:rgba(234,179,8,0.40)] bg-[color:rgba(234,179,8,0.08)]",
  resolved: "text-[color:var(--success)] border-[color:rgba(34,197,94,0.35)] bg-[color:rgba(34,197,94,0.08)]",
  closed: "text-[color:var(--text-3)] border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]",
  archived: "text-[color:var(--text-3)] border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]",
};

const PRIORITY_COLORS = {
  urgent: "text-[color:var(--danger)]",
  high: "text-[color:var(--accent-warm)]",
  normal: "text-[color:var(--text-2)]",
  low: "text-[color:var(--text-3)]",
};

function fmtStamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function TicketDetailDialog({ ticketId, open, onOpenChange, onChanged }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/support/tickets/${ticketId}`);
      setTicket(res?.data || null);
    } catch (_e) {
      toast.error("Could not load the ticket");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const setStatus = async (status) => {
    setSavingStatus(true);
    try {
      const res = await api.patch(`/admin/support/tickets/${ticketId}`, { status });
      setTicket(res?.data || null);
      toast.success(`Marked ${status.replace("_", " ")}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update ticket");
    } finally {
      setSavingStatus(false);
    }
  };

  const setPriority = async (priority) => {
    try {
      const res = await api.patch(`/admin/support/tickets/${ticketId}`, { priority });
      setTicket(res?.data || null);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update priority");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const res = await api.post(`/admin/support/tickets/${ticketId}/notes`, {
        body: noteText.trim(),
      });
      setTicket(res?.data || null);
      setNoteText("");
      toast.success("Internal note added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add note");
    } finally {
      setAddingNote(false);
    }
  };

  const archive = async () => {
    setArchiving(true);
    try {
      await api.delete(`/admin/support/tickets/${ticketId}`);
      toast.success("Ticket archived");
      onChanged?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not archive");
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
        data-testid="admin-ticket-detail"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[color:var(--brand-primary)]" />
            Ticket details
          </DialogTitle>
        </DialogHeader>
        {loading || !ticket ? (
          <div className="text-sm text-[color:var(--text-3)] py-6">Loading…</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
                {ticket.subject}
              </div>
              <div className="text-[11px] text-[color:var(--text-3)] mt-1 font-mono">
                {ticket.ticket_id}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                  User
                </div>
                <div className="text-[color:var(--text-1)] mt-1">{ticket.user_email}</div>
                <div className="text-[color:var(--text-3)] font-mono text-[11px]">
                  {ticket.user_id}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                  Plan
                </div>
                <div className="text-[color:var(--text-1)] mt-1 capitalize">
                  {ticket.user_plan || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                  Category
                </div>
                <div className="text-[color:var(--text-1)] mt-1">{ticket.category_label}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                  Submitted
                </div>
                <div className="text-[color:var(--text-1)] mt-1">
                  {fmtStamp(ticket.created_at)}
                </div>
              </div>
              {ticket.metadata?.platform ? (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                    Platform
                  </div>
                  <div className="text-[color:var(--text-1)] mt-1">
                    {ticket.metadata.platform}
                  </div>
                </div>
              ) : null}
              {ticket.metadata?.related_post_id ? (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
                    Related post
                  </div>
                  <div className="text-[color:var(--text-1)] mt-1 font-mono text-[11px]">
                    {ticket.metadata.related_post_id}
                  </div>
                </div>
              ) : null}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)] mb-1">
                Message
              </div>
              <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-3 whitespace-pre-wrap text-[color:var(--text-2)] leading-relaxed">
                {ticket.message}
              </div>
            </div>

            {ticket.attachment ? (
              <div data-testid="admin-ticket-attachment">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)] mb-1">
                  Attachment
                </div>
                <SupportAttachmentViewer
                  apiPath={`/admin/support/tickets/${ticket.ticket_id}/attachment`}
                  filename={ticket.attachment.filename}
                  sizeBytes={ticket.attachment.size_bytes}
                  testId="admin-ticket-attachment"
                />
              </div>
            ) : ticket.screenshot_url ? (
              <div className="text-xs">
                <span className="text-[color:var(--text-3)]">Screenshot:</span>{" "}
                <a
                  href={ticket.screenshot_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[color:var(--brand-primary)] hover:underline break-all"
                  data-testid="admin-ticket-screenshot"
                >
                  {ticket.screenshot_url}
                </a>
              </div>
            ) : null}

            <div className="text-[11px] text-[color:var(--text-3)] space-y-0.5">
              <div>Page URL: <span className="text-[color:var(--text-2)] break-all">{ticket.metadata?.page_url || "—"}</span></div>
              <div>User agent: <span className="text-[color:var(--text-2)] break-all">{ticket.metadata?.user_agent || "—"}</span></div>
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Select value={ticket.status} onValueChange={setStatus}>
                <SelectTrigger
                  className="w-[160px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                  data-testid="admin-ticket-status-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ticket.priority} onValueChange={setPriority}>
                <SelectTrigger
                  className="w-[160px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                  data-testid="admin-ticket-priority-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              {savingStatus ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--text-3)]" />
              ) : null}
              <div className="ml-auto">
                <Button
                  variant="outline"
                  onClick={archive}
                  disabled={archiving || ticket.status === "archived"}
                  className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                  data-testid="admin-ticket-archive"
                >
                  {archiving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Archive className="h-4 w-4 mr-2" />
                  )}
                  Archive
                </Button>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)] mb-1">
                Internal notes ({ticket.internal_notes?.length || 0})
              </div>
              <div className="space-y-2">
                {(ticket.internal_notes || []).map((n) => (
                  <div
                    key={n.note_id}
                    className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5 text-xs"
                  >
                    <div className="text-[10px] text-[color:var(--text-3)] mb-1">
                      {n.author_email} · {fmtStamp(n.created_at)}
                    </div>
                    <div className="text-[color:var(--text-2)] whitespace-pre-wrap leading-relaxed">
                      {n.body}
                    </div>
                  </div>
                ))}
              </div>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add an internal note (visible only to admins)…"
                rows={2}
                className="mt-2 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                data-testid="admin-ticket-note-input"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  onClick={addNote}
                  disabled={addingNote || !noteText.trim()}
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                  data-testid="admin-ticket-note-submit"
                >
                  {addingNote ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Add note
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, resolved: 0, archived: 0 });
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [emailMeta, setEmailMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [search, setSearch] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterCategory !== "all") params.set("category", filterCategory);
      if (filterPriority !== "all") params.set("priority", filterPriority);
      if (search.trim()) params.set("search", search.trim());
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [list, emailStatus] = await Promise.all([
        api.get(`/admin/support/tickets${qs}`),
        api.get("/admin/support/email-status").catch(() => null),
      ]);
      setTickets(list?.data?.tickets || []);
      setCounts(list?.data?.counts_by_status || {});
      setEmailConfigured(list?.data?.email_configured ?? true);
      setEmailMeta(emailStatus?.data || null);
    } catch (_e) {
      toast.error("Could not load support tickets");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory, filterPriority, search]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totalActive = useMemo(
    () => (counts.open || 0) + (counts.in_progress || 0),
    [counts],
  );

  return (
    <div className="space-y-6 pb-10" data-testid="admin-support-page">
      <PageHeader
        title="Support tickets"
        subtitle="Triage customer-reported problems and track them through to resolution."
        action={
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading}
            className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
            data-testid="admin-support-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {!emailConfigured ? (
        <div
          className="rounded-xl border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-4 flex items-start gap-3"
          data-testid="admin-support-email-warning"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 text-[color:var(--accent-warm)] shrink-0" />
          <div className="text-sm text-[color:var(--text-2)]">
            <div className="font-medium text-[color:var(--text-1)]">
              Email notifications are not configured yet
            </div>
            <div className="mt-1 text-xs text-[color:var(--text-3)] leading-relaxed">
              Tickets are still saved to the database and visible below. To enable owner email
              alerts, set <code className="font-mono">EMAIL_PROVIDER_API_KEY</code> and{" "}
              <code className="font-mono">
                {emailMeta?.recipient_env_var || "SUPPORT_TICKET_RECIPIENT"}
              </code>{" "}
              in the backend env. Provider defaults to SendGrid (override via{" "}
              <code className="font-mono">SUPPORT_EMAIL_PROVIDER</code>).
            </div>
            {emailMeta ? (
              <div className="mt-2 text-[11px] text-[color:var(--text-3)] font-mono">
                to: {emailMeta.to || "—"} · from: {emailMeta.from || "—"} · provider:{" "}
                {emailMeta.provider}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { id: "open", label: "Open", n: counts.open || 0, tone: STATUS_COLORS.open },
          { id: "in_progress", label: "In Progress", n: counts.in_progress || 0, tone: STATUS_COLORS.in_progress },
          { id: "resolved", label: "Resolved", n: counts.resolved || 0, tone: STATUS_COLORS.resolved },
          { id: "archived", label: "Archived", n: counts.archived || 0, tone: STATUS_COLORS.archived },
        ].map((c) => (
          <button
            key={c.id}
            onClick={() => setFilterStatus(c.id)}
            className={`rounded-xl border p-3 text-left ${c.tone} ${filterStatus === c.id ? "ring-1 ring-[color:var(--brand-primary)]" : ""}`}
            data-testid={`admin-support-stat-${c.id}`}
          >
            <div className="text-[10px] uppercase tracking-[0.16em]">{c.label}</div>
            <div className="font-display text-2xl font-semibold mt-1">{c.n}</div>
          </button>
        ))}
      </div>
      <div className="text-[11px] text-[color:var(--text-3)]">
        {totalActive} active ticket{totalActive === 1 ? "" : "s"} require attention.
      </div>

      <GlassCard className="p-4" data-testid="admin-support-filters">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]" data-testid="admin-support-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]" data-testid="admin-support-filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
              {CATEGORY_OPTIONS.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]" data-testid="admin-support-filter-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
              {PRIORITY_OPTIONS.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[color:var(--text-3)]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject, message, email…"
              className="pl-8 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
              data-testid="admin-support-search"
            />
          </div>
        </div>
      </GlassCard>

      <div className="space-y-2" data-testid="admin-support-tickets">
        {loading ? (
          <div className="text-sm text-[color:var(--text-3)] py-6">Loading…</div>
        ) : tickets.length === 0 ? (
          <GlassCard className="p-6 text-center" data-testid="admin-support-empty">
            <div className="text-sm text-[color:var(--text-2)]">
              No tickets match the current filters.
            </div>
          </GlassCard>
        ) : (
          tickets.map((t) => (
            <button
              key={t.ticket_id}
              onClick={() => setOpenId(t.ticket_id)}
              className="w-full text-left rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] hover:bg-[color:var(--surface-2)] transition-colors p-4"
              data-testid={`admin-support-row-${t.ticket_id}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-sm font-semibold text-[color:var(--text-1)]">
                      {t.subject}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status] || STATUS_COLORS.open}`}
                    >
                      {t.status_label}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-[0.14em] ${PRIORITY_COLORS[t.priority] || PRIORITY_COLORS.normal}`}
                    >
                      {t.priority_label}
                    </span>
                  </div>
                  <div className="text-[11px] text-[color:var(--text-3)] mt-1">
                    {t.category_label} · {t.user_email} · {fmtStamp(t.created_at)}
                  </div>
                  <div className="text-xs text-[color:var(--text-2)] mt-1 line-clamp-2">
                    {t.message}
                  </div>
                </div>
                <div className="text-[10px] text-[color:var(--text-3)] font-mono shrink-0">
                  {t.ticket_id}
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      <TicketDetailDialog
        ticketId={openId}
        open={!!openId}
        onOpenChange={(v) => !v && setOpenId(null)}
        onChanged={refresh}
      />
    </div>
  );
}
